import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { combineLatest, Observable } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import {
    BaseContainer,
    DialogConfig,
    DialogRef,
    ErrorResponse,
    MultiFormSelectOptions,
    NotificationType,
    Toast
} from '@stylehaven/components';
import { CustomValidators } from '../../../helpers';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { GroupId } from '../../../record/entities/metadata.entity';
import { SavedQuery } from '../../../entities/saved-queries/saved-query.entity';
import { SavedQueriesSandboxService } from '../../sandbox/saved-queries.sandbox';

export interface UpdateQueryDialogData {
    query: SavedQuery;
    currentQuery: string;
}

@Component({
    standalone: false,
    selector: 'digihaven-update-query-dialog',
    templateUrl: './update-query-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateQueryDialogComponent extends BaseContainer implements OnInit, OnDestroy {
    readonly NotificationType = NotificationType;
    readonly ErrorResponse = ErrorResponse;

    readonly updateLoading$ = new LoadingBehaviorSubject();

    /** Whether to show the "this changes it for everyone" warning — the groups control lets the user un-share it here too. */
    
    
    isShared: boolean;

    updateQueryForm!: UntypedFormGroup;
    groups$!: Observable<MultiFormSelectOptions>;
    canUpdate$!: Observable<boolean>;

    constructor(
        private readonly dialogConfig: DialogConfig<UpdateQueryDialogData>,
        private readonly dialogRef: DialogRef<SavedQuery>,
        private readonly sandbox: SavedQueriesSandboxService,
        private readonly formBuilder: UntypedFormBuilder
    ) {
        super();
    }

    ngOnInit(): void {
        this.isShared = this.dialogConfig.data!.query.Groups.length > 0;
        this.updateQueryForm = this.formBuilder.group({
            name: [this.dialogConfig.data!.query.Name, [Validators.required, CustomValidators.notBlank]],
            groups: [this.dialogConfig.data!.query.Groups]
        });

        this.groups$ = this.sandbox.getGroupOptions();

        this.canUpdate$ = combineLatest([
            this.updateQueryForm.statusChanges.pipe(map(status => status === 'VALID')),
            this.updateLoading$
        ]).pipe(
            map(([isValid, isLoading]) => this.dialogConfig.data!.query.Context.IsEditable && isValid && !isLoading)
        );
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.updateLoading$.complete();
    }

    update(): void {
        if (!this.dialogConfig.data!.query.Context.IsEditable) {
            return;
        }

        this.updateLoading$.startLoading();

        const toast: Toast = this.sandbox.addUpdatingToast();
        const { query, currentQuery } = this.dialogConfig.data!;
        const name: string = this.nameControl.value;
        const groups: GroupId[] = this.groupsControl.value || [];

        this.sandbox
            .updateQuery(query.Id, { Name: name, Groups: groups, Query: currentQuery })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.updateLoading$.stopLoading();

                    // The PUT returns 204 with no body, so the updated query is assembled from what we already
                    // know client-side — the container re-applies it (ticket 07) so the "changed" indicator
                    // compares against exactly what was just saved, the same way a fresh save does.
                    this.dialogRef.close(
                        new SavedQuery({
                            Id: query.Id,
                            Name: name,
                            Groups: groups,
                            Query: currentQuery,
                            CreatedBy: query.CreatedBy,
                            OrganisationId: query.OrganisationId,
                            Context: query.Context
                        })
                    );
                    this.sandbox.updateUpdatingToastSuccess(toast.id);
                },
                error: () => {
                    this.updateLoading$.stopLoading();
                    this.sandbox.updateUpdatingToastFailure(toast.id);
                }
            });
    }

    close(): void {
        this.dialogRef.close();
    }

    get nameControl(): UntypedFormControl {
        return this.updateQueryForm.get('name') as UntypedFormControl;
    }

    get groupsControl(): UntypedFormControl {
        return this.updateQueryForm.get('groups') as UntypedFormControl;
    }
}
